import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import useDebouncedValue from './useDebouncedValue';
import { getDateRangeFromPreset } from './dateUtils';

// Default filter values
const DEFAULT_FILTERS = {
  // Core filters
  project: '',
  dateRange: '',
  startDate: '',
  endDate: '',
  status: '',
  environment: '',
  branch: '',
  group: '',
  user: '',
  
  // Pagination
  page: 1,
  pageSize: 20,
  
  // Time aggregation
  period: 'daily',
  
  // Comparison
  compareTo: false,
};

export default function useComprehensiveFilterState(initialFilters = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Initialize filters with defaults and URL params
  const [filters, setFilters] = useState(() => {
    const initial = { ...DEFAULT_FILTERS, ...initialFilters };
    const urlFilters = {};
    
    // Load from URL params
    Object.keys(initial).forEach(key => {
      const value = searchParams.get(key);
      if (value !== null) {
        if (key === 'page' || key === 'pageSize') {
          urlFilters[key] = parseInt(value);
        } else if (key === 'compareTo') {
          urlFilters[key] = value === 'true';
        } else {
          urlFilters[key] = value;
        }
      }
    });
    
    const finalFilters = { ...initial, ...urlFilters };
    
    // If dateRange is set but startDate/endDate are not, convert the preset
    if (finalFilters.dateRange && !finalFilters.startDate && !finalFilters.endDate) {
      const { startDate, endDate } = getDateRangeFromPreset(finalFilters.dateRange);
      finalFilters.startDate = startDate;
      finalFilters.endDate = endDate;
    }
    
    return finalFilters;
  });

  // Debounced values for API calls - create individual debounced values
  const debouncedProject = useDebouncedValue(filters.project, 300);
  const debouncedDateRange = useDebouncedValue(filters.dateRange, 300);
  const debouncedStartDate = useDebouncedValue(filters.startDate, 300);
  const debouncedEndDate = useDebouncedValue(filters.endDate, 300);
  const debouncedStatus = useDebouncedValue(filters.status, 300);
  const debouncedEnvironment = useDebouncedValue(filters.environment, 300);
  const debouncedBranch = useDebouncedValue(filters.branch, 300);
  const debouncedGroup = useDebouncedValue(filters.group, 300);
  const debouncedUser = useDebouncedValue(filters.user, 300);
  const debouncedPeriod = useDebouncedValue(filters.period, 300);

  const debouncedFilters = useMemo(() => ({
    project: debouncedProject,
    dateRange: debouncedDateRange,
    startDate: debouncedStartDate,
    endDate: debouncedEndDate,
    status: debouncedStatus,
    environment: debouncedEnvironment,
    branch: debouncedBranch,
    group: debouncedGroup,
    user: debouncedUser,
    period: debouncedPeriod,
    page: filters.page,
    pageSize: filters.pageSize,
    compareTo: filters.compareTo,
  }), [
    debouncedProject, debouncedDateRange, debouncedStartDate, debouncedEndDate,
    debouncedStatus, debouncedEnvironment, debouncedBranch, debouncedGroup,
    debouncedUser, debouncedPeriod, filters.page, filters.pageSize, filters.compareTo
  ]);

  // Update a single filter
  const updateFilter = useCallback((key, value) => {
    setFilters(prev => {
      const newFilters = { ...prev, [key]: value };
      
      // Reset page when filters change (except pagination filters)
      if (key !== 'page' && key !== 'pageSize') {
        newFilters.page = 1;
      }
      
      return newFilters;
    });
  }, []);

  // Reset all filters to defaults
  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, []);

  // Sync filters to URL
  useEffect(() => {
    const next = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '' && value !== false) {
        next.set(key, String(value));
      }
    });
    setSearchParams(next, { replace: true });
  }, [filters, setSearchParams]);

  // Build API query parameters
  const buildApiParams = useCallback(() => {
    const params = {};
    Object.entries(debouncedFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '' && value !== false) {
        params[key] = value;
      }
    });
    return params;
  }, [debouncedFilters]);

  return {
    filters,
    debouncedFilters,
    updateFilter,
    resetFilters,
    buildApiParams,
  };
}
